import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMachine } from "@xstate/react";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { CACHE_KEYS } from "@bn/api/apiv1";
import { LoadingPlaceHolder } from "@components";
import { SomethingWrong } from "./steps/SomethingWrong";
import { PriceAndDateStep } from "./steps/PriceAndDateStep";
import { BookingValues } from "./stateMachine/types/bookingValues";
import { SummaryStep } from "./steps/SummaryStep";
import { useStateHelpers } from "./hooks/useStateHelpers";
import { editTransactionWizardSM } from "./stateMachine/editTransactionWizardSM";
import { BookingWizardHeader } from "./components/BookingWizardHeader";

export interface Props {
  id: number;
  image?: string | null;
  onSuccess?: () => void;
}

export const EditTransactionWizard = ({ id, image, onSuccess }: Props) => {
  const { t } = useTranslation();
  const [currentState, sendEvent] = useMachine(editTransactionWizardSM);
  const { isLoadingStateForModals } = useStateHelpers(currentState);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const backOneStep = () => sendEvent({ type: "BACK" });

  const investmentValues = currentState.context.investmentValues;
  const investmentName = investmentValues?.name ?? "";
  const bookingType = currentState.context.bookingValues?.type;
  const isPersisted = currentState.matches("persisted");
  const isValidating = currentState.matches("validateBooking");
  const isPersisting = currentState.matches("persist");

  const header = (
    <BookingWizardHeader
      name={investmentValues?.name}
      isin={investmentValues?.isin}
      wkn={investmentValues?.wkn}
      type={investmentValues?.type}
      image={image}
      bookingType={bookingType}
    />
  );

  useEffect(() => {
    if (!isPersisted) return;
    enqueueSnackbar(
      `${t("transactionWizard.persistedStep.title")}: ${investmentName}`,
      { autoHideDuration: 3000, variant: "success" }
    );
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_ACCOUNTS],
    });
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_INVESTMENTS],
    });
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_BOOKINGS],
    });
    onSuccess?.();
  }, [isPersisted, enqueueSnackbar, queryClient, t, investmentName, onSuccess]);

  return (
    <>
      {isLoadingStateForModals() && <LoadingPlaceHolder height="300px" />}

      {currentState.matches("idle") &&
        sendEvent({ type: "INIT_FROM_BOOKING", id: id }) && (
          <LoadingPlaceHolder height="300px" />
        )}

      {(currentState.matches("priceAndDate") || isValidating) && (
        <PriceAndDateStep
          handleNext={(bookingValues: BookingValues) =>
            sendEvent({
              type: "VALIDATE_BOOKING",
              bookingValues,
            })
          }
          bookingValues={currentState.context.bookingValues}
          bookingValuesToRestore={currentState.context.bookingValuesToRestore}
          investmentValues={currentState.context.investmentValues}
          validationError={currentState.context.validationError}
          isFetching={isValidating}
          isEditTransaction
          headerContent={header}
        />
      )}

      {(currentState.matches("summary") || isPersisting) && (
        <SummaryStep
          handleNext={() =>
            sendEvent({
              type: "PERSIST",
            })
          }
          handlePrevious={backOneStep}
          bookingValues={currentState.context.bookingValues}
          investmentType={
            currentState.context.investmentValues.type || "11_stock"
          }
          investmentCurrency={
            currentState.context.investmentValues.investmentCurrency || "EUR"
          }
          headerContent={header}
          isFetching={isPersisting}
        />
      )}

      {currentState.matches("somethingWrong") && (
        <SomethingWrong message={currentState.context.error?.message} />
      )}
    </>
  );
};
